// Core
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  Chip,
  Crumbs,
  DataTable,
  FieldSelect,
  FieldText,
  KpiStrip,
  Modal,
  PageHead,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";
import { versionBasePath } from "components/VersionManagementButtons";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  selectAllMeters,
  useAddNewMeterMutation,
  useDeleteMeterByIdMutation,
  useGetMetersByCommunityIdQuery,
} from "state/meters/metersApiSlice";
import {
  selectAllResources,
  useGetResourcesByCommunityIdQuery,
} from "state/resources/resourcesApiSlice";

import { meterTypes, meterInstallationTypes } from "utils/constants";
import { translateOptions } from "utils/constants/enumLabels";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

const TYPE_TONES = {
  CPE: "teal",
  AGGREGATOR_METER: "info",
  INTERNAL: "gray",
};

function ListMeters() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["meters", "community", "common"]);

  // READ meters
  const community = useSelector(selectCurrentCommunity);
  const { meters } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ meters: (data && selectAllMeters(data)) || [] }),
    }
  );

  // READ resources (for the associated-resource column)
  const { resources } = useGetResourcesByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: resourcesList }) => ({
        resources: resourcesList && selectAllResources(resourcesList),
      }),
    }
  );

  // Modal
  const [open, setOpen] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [ord, setOrd] = useState("E-REDES");
  const [meterType, setMeterType] = useState("CPE");
  const [meterInstallationType, setMeterInstallationType] = useState("IC");
  const [productionIdentifier, setProductionIdentifier] = useState("");
  const [startDate, setStartDate] = useState(lisbonToday());
  const [endDate, setEndDate] = useState(() => defaultBusinessEndDate(lisbonToday()));
  const [CPEForNewMeter, setCPEForNewMeter] = useState({});
  const handleClose = () => {
    setOpen(false);
    setIdentifier("");
    setOrd("E-REDES");
    setMeterType("CPE");
    setMeterInstallationType("IC");
    setProductionIdentifier("");
    const resetStartDate = lisbonToday();
    setStartDate(resetStartDate);
    setEndDate(defaultBusinessEndDate(resetStartDate));
    setCPEForNewMeter({});
  };

  // CREATE METER
  const onCreateMeterClicked = () => {
    setOpen(true);
  };
  const [addMeter] = useAddNewMeterMutation();
  const onCreateMeter = async () => {
    try {
      await addMeter({
        community_id: community.id,
        identifier,
        ord,
        installation_type: meterInstallationType,
        type: meterType,
        cpe_id: CPEForNewMeter?.id,
        production_identifier:
          meterType === "CPE" && meterInstallationType === "IAC" ? productionIdentifier : null,
        start_date: startDate,
        end_date: endDate || null,
      }).unwrap();
      return handleClose();
    } catch (err) {
      return dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // DELETE Meter
  const [deleteMeter] = useDeleteMeterByIdMutation();
  const onDeleteMeterClicked = async (meterIdSelected) => {
    const action = async () => {
      try {
        const response = await deleteMeter({
          communityId: community.id,
          id: meterIdSelected,
        }).unwrap();
        if (response) {
          dispatch(
            openNotificationPopup({
              notification: response.message,
              type: "error",
            })
          );
        } else {
          dispatch(
            openNotificationPopup({
              notification: t("notifications.deleted_success"),
              type: "success",
            })
          );
        }
      } catch (err) {
        dispatch(openNotificationPopup({ notification: err, type: "error" }));
      }
    };

    dispatch(openWarningAlert({ confirmText: t("confirm_remove"), confirmAction: action }));
  };

  const typeLabel = (type) => t(`table.filters.type.${type}`, { defaultValue: type });
  const instLabel = (it) => t(`table.filters.installation_type.${it}`, { defaultValue: it });

  const rows = useMemo(
    () =>
      meters.map((meter) => {
        const resourceNames = (resources ?? [])
          .filter((resource) => resource.meter_id === meter.id)
          .map((resource) => resource.name)
          .join(", ");
        return {
          id: meter.id,
          identifier: meter.identifier,
          type: meter.type,
          installation_type: meter.installation_type,
          ord: meter.ord ?? "—",
          is_host: !!meter.is_host,
          cpe: meter.cpe_id ? meters.find((m) => m.id === meter.cpe_id)?.identifier ?? "—" : "—",
          resource: resourceNames || "—",
        };
      }),
    [meters, resources]
  );

  const hasContracts = (type) => type === "CPE" || type === "AGGREGATOR_METER";

  const columns = [
    {
      key: "identifier",
      label: t("table.columns.meter"),
      render: (v) => (
        <div className="rec-cell-member">
          <div
            className="rec-av-sm"
            style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}
          >
            <Icon name="electric_meter" style={{ fontSize: 18 }} />
          </div>
          <span className="font-[family-name:var(--mono)] text-[12.5px] text-ink">{v}</span>
        </div>
      ),
    },
    {
      key: "type",
      label: t("table.columns.type"),
      render: (v) => <Chip tone={TYPE_TONES[v] || "gray"}>{typeLabel(v)}</Chip>,
    },
    {
      key: "installation_type",
      label: t("table.columns.installation_type"),
      render: (v) => instLabel(v),
    },
    { key: "ord", label: t("table.columns.ord") },
    {
      key: "is_host",
      label: t("table.columns.is_host"),
      render: (v) =>
        v ? (
          <Chip tone="green">{t("table.badges.yes")}</Chip>
        ) : (
          <Chip tone="gray">{t("table.badges.no")}</Chip>
        ),
    },
    { key: "cpe", label: t("table.columns.cpe") },
    { key: "resource", label: t("table.columns.resource") },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          {hasContracts(row.type) && (
            <button
              type="button"
              className="rec-icon-btn"
              title={t("table.columns.contracts")}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/communities/${community.id}/infrastructure/meters/${row.id}`);
              }}
            >
              <Icon name="receipt_long" />
            </button>
          )}
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.edit")}
            onClick={(e) => {
              e.stopPropagation();
              navigate(versionBasePath(community.id, "meter", row.id), {
                state: {
                  label: row.identifier,
                  returnTo: `/communities/${community.id}/infrastructure/meters`,
                },
              });
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.delete")}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteMeterClicked(row.id);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="meters" />
      <Crumbs
        items={[
          { label: community?.name || "—" },
          {
            label: t("sidenav.infrastructure", { ns: "community", defaultValue: "Infraestrutura" }),
          },
          { label: t("title") },
        ]}
      />
      <PageHead
        title={t("title")}
        subtitle={t("subtitle")}
        actions={
          <Button onClick={onCreateMeterClicked}>
            <Icon name="add" />
            {t("new_button")}
          </Button>
        }
      />

      <KpiStrip
        className="mb-rec"
        cols={4}
        items={[
          {
            icon: "electric_meter",
            value: String(meters.length),
            label: t("stats.total"),
            tint: "teal",
          },
          {
            icon: "bolt",
            value: String(meters.filter((m) => m.type === "CPE").length),
            label: t("stats.cpe"),
            tint: "info",
          },
          {
            icon: "lan",
            value: String(meters.filter((m) => m.type === "INTERNAL").length),
            label: t("stats.internal"),
            tint: "amber",
          },
          {
            icon: "cottage",
            value: String(meters.filter((m) => m.is_host).length),
            label: t("stats.host"),
            tint: "green",
          },
        ]}
      />

      <div className="rec-card">
        <DataTable
          columns={columns}
          rows={rows}
          onRow={(row) =>
            navigate(versionBasePath(community.id, "meter", row.id), {
              state: {
                label: row.identifier,
                returnTo: `/communities/${community.id}/infrastructure/meters`,
              },
            })
          }
          search={t("search_placeholder")}
          searchKeys={["identifier", "type", "resource", "cpe"]}
        />
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        title={t("modal.new.title")}
        maxWidth={460}
        footer={
          <>
            <Button variant="ghost" onClick={handleClose}>
              {t("new.cancel", { ns: "community" })}
            </Button>
            <Button
              disabled={!identifier || !startDate || (endDate && endDate < startDate)}
              onClick={onCreateMeter}
            >
              {t("new.create", { ns: "community" })}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-[14px]">
          <FieldText
            label={t("modal.new.identifier")}
            value={identifier}
            onChange={setIdentifier}
          />
          <FieldSelect
            label={t("modal.new.installation_type")}
            value={meterInstallationType}
            options={translateOptions(meterInstallationTypes, "meterInstallation")}
            onChange={setMeterInstallationType}
          />
          {meterType === "CPE" && meterInstallationType === "IAC" && (
            <FieldText
              label={t("modal.new.production_identifier")}
              value={productionIdentifier}
              onChange={setProductionIdentifier}
            />
          )}
          {(meterType === "CPE" || meterType === "AGGREGATOR_METER") && (
            <FieldSelect
              label={t("modal.new.ord")}
              value={ord}
              options={[{ value: "E-REDES", label: "E-Redes" }]}
              onChange={setOrd}
            />
          )}
          {meterType === "INTERNAL" && (
            <FieldSelect
              label={t("modal.new.cpe")}
              value={CPEForNewMeter?.id ?? ""}
              options={[
                { label: "—", value: "" },
                ...meters
                  .filter((meter) => meter.type === "CPE")
                  .map((meter) => ({ label: meter.identifier, value: meter.id })),
              ]}
              onChange={(v) => setCPEForNewMeter(v ? meters.find((meter) => meter.id === v) : {})}
            />
          )}
          <FieldSelect
            label={t("modal.new.type")}
            value={meterType}
            options={translateOptions(meterTypes, "meterType")}
            onChange={setMeterType}
          />
          <div className="grid grid-cols-2 gap-[14px]">
            <ValidityDateRangePicker
              className="col-span-2"
              value={{ start: startDate, end: endDate }}
              onChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ListMeters;
